/**
 * Entity factories: construct new domain entities with sensible defaults and
 * fresh sync metadata (decision #20). Centralizing this keeps every creation
 * path consistent (every entity always has valid SyncMeta) and gives the web
 * app a single, platform-neutral way to mint entities.
 *
 * Convention: spread the caller's `init` FIRST, then apply generated id/sync
 * and defaults. This way the generated id/sync win, defaults fill any gaps,
 * and explicitly-passed fields in `init` are preserved.
 */
import type {
  AssetId,
  Character,
  Chat,
  ChatParticipant,
  GameCard,
  GeneratedImage,
  Lorebook,
  LorebookEntry,
  Message,
  MessageVariant,
  Persona,
  Preset,
  PromptBlock,
  ProviderProfile,
  Script,
} from "../domain/index.js";
import { ROLEPLAY_MODULE_ID } from "../domain/index.js";
import type { GameDefinition } from "../game/game-definition.js";
import { gameDefinitionToRoqeetJson } from "../game/roqeet-game-document.js";
import {
  newCharacterId,
  newChatId,
  newGameCardId,
  newGeneratedImageId,
  newLorebookEntryId,
  newLorebookId,
  newMessageId,
  newParticipantId,
  newPersonaId,
  newPresetId,
  newProviderProfileId,
  newScriptId,
} from "../util/id.js";
import { newSyncMeta } from "../util/sync-meta.js";

export function createCharacter(init: Partial<Character> & { name: string }): Character {
  return {
    ...init,
    id: newCharacterId(),
    sync: newSyncMeta(),
    name: init.name,
    description: init.description ?? "",
    firstMessage: init.firstMessage ?? "",
    alternateGreetings: init.alternateGreetings ?? [],
    scriptIds: init.scriptIds ?? [],
    tags: init.tags ?? [],
    rating: init.rating ?? "unrated",
  };
}

export function createPersona(init: Partial<Persona> & { name: string }): Persona {
  return {
    ...init,
    id: newPersonaId(),
    sync: newSyncMeta(),
    name: init.name,
    description: init.description ?? "",
  };
}

/** A default, opinionated prompt pipeline (decision #12) for new presets. */
export function defaultPromptBlocks(): PromptBlock[] {
  const mk = (type: PromptBlock["type"], role: PromptBlock["role"]): PromptBlock => ({
    id: type,
    type,
    enabled: true,
    role,
  });
  return [
    mk("system", "system"),
    mk("persona", "system"),
    mk("char-description", "system"),
    mk("scenario", "system"),
    mk("lorebook-before", "system"),
    mk("example-dialogue", "system"),
    mk("chat-history", "user"),
    mk("lorebook-after", "system"),
    mk("author-note", "system"),
    mk("post-history", "system"),
  ];
}

export function createPreset(init: Partial<Preset> & { name: string }): Preset {
  return {
    ...init,
    id: newPresetId(),
    sync: newSyncMeta(),
    name: init.name,
    apiStyle: init.apiStyle ?? "chat",
    sampling: init.sampling ?? { temperature: 0.9, maxTokens: 512, contextSize: 8192 },
    blocks: init.blocks ?? defaultPromptBlocks(),
  };
}

/** A configured connection to an AI provider (decision #7). Key lives in the
 *  vault and is referenced by keyRef; never stored on the profile. */
export function createProviderProfile(
  init: Partial<ProviderProfile> & { name: string; kind: ProviderProfile["kind"] },
): ProviderProfile {
  return {
    ...init,
    id: newProviderProfileId(),
    sync: newSyncMeta(),
    name: init.name,
    kind: init.kind,
    transport: init.transport ?? "direct",
    enabled: init.enabled ?? true,
  };
}

/** A single lorebook entry with id + sane defaults (decision #6 import). */
export function createLorebookEntry(
  init: Partial<LorebookEntry> & { content: string },
): LorebookEntry {
  return {
    ...init,
    id: newLorebookEntryId(),
    keys: init.keys ?? [],
    selectiveLogic: init.selectiveLogic ?? "and-any",
    content: init.content,
    constant: init.constant ?? false,
    disabled: init.disabled ?? false,
    insertionOrder: init.insertionOrder ?? 0,
    position: init.position ?? "before-char",
  };
}

/** A standalone or embedded lorebook (decision #6 import maps world info here). */
export function createLorebook(init: Partial<Lorebook> & { name: string }): Lorebook {
  return {
    ...init,
    id: newLorebookId(),
    sync: newSyncMeta(),
    name: init.name,
    entries: init.entries ?? [],
    scanDepth: init.scanDepth ?? 4,
    recursiveScanning: init.recursiveScanning ?? false,
  };
}

/**
 * A creator script (decisions #4/#5/#17). For language === "regex" it's a pure
 * find/replace display script needing no runtime; JS/Lua run sandboxed later.
 * Elevated capabilities stay inert until consentGranted (decision #17).
 */
export function createScript(
  init: Partial<Script> & { name: string; language: Script["language"]; source: string },
): Script {
  return {
    ...init,
    id: newScriptId(),
    sync: newSyncMeta(),
    name: init.name,
    language: init.language,
    enabled: init.enabled ?? true,
    source: init.source,
  };
}

/**
 * A generated-image provenance record (decision #19). Stamped at generation
 * time so the gallery can index every image — Risu can't do this because it
 * lacks the metadata index. `assetId` points at the content-addressed blob.
 */
export function createGeneratedImage(
  init: Partial<GeneratedImage> & { assetId: AssetId; provider: string },
): GeneratedImage {
  return {
    ...init,
    id: newGeneratedImageId(),
    sync: newSyncMeta(),
    assetId: init.assetId,
    provider: init.provider,
    generatedAt: init.generatedAt ?? Date.now(),
  };
}

/** Build a 1:1 chat between one persona and one character (decision #22). */
export function createChat(
  init: Partial<Chat> & {
    title: string;
    characterId: Chat["participants"][number]["characterId"];
  },
): Chat {
  // `characterId` is a convenience input, not a Chat field — keep it out of
  // the spread so no stray property lands on the entity at runtime.
  const { characterId, ...rest } = init;
  const characterParticipant: ChatParticipant = {
    id: newParticipantId(),
    type: "character",
    characterId,
  };
  return {
    ...rest,
    id: newChatId(),
    sync: newSyncMeta(),
    title: init.title,
    // 새 세션은 모듈을 명시한다(결정 #28). 미지정 시 롤플레잉(모듈 #1).
    moduleId: init.moduleId ?? ROLEPLAY_MODULE_ID,
    participants: init.participants ?? [characterParticipant],
    turnStrategy: init.turnStrategy ?? "manual",
    lorebookIds: init.lorebookIds ?? [],
  };
}

export function createMessageVariant(
  init: Partial<MessageVariant> & { content: string },
): MessageVariant {
  return {
    ...init,
    content: init.content,
    createdAt: init.createdAt ?? Date.now(),
  };
}

export function createMessage(
  init: Partial<Message> & {
    chatId: string;
    senderType: Message["senderType"];
    content: string;
    order: number;
  },
): Message {
  // `content` is a convenience input used to seed the first variant; it is not
  // a Message field, so destructure it out before spreading.
  const { content, ...rest } = init;
  return {
    ...rest,
    id: newMessageId(),
    sync: newSyncMeta(),
    chatId: init.chatId,
    senderType: init.senderType,
    variants: init.variants ?? [createMessageVariant({ content })],
    activeVariant: init.activeVariant ?? 0,
    order: init.order,
  };
}

/**
 * 데이터형 게임 카드를 만든다(결정 #29). 핵심 안전장치: definition.id를 카드의
 * UUID로 강제 시드한다 — 이게 런타임 moduleId(결정 #28)가 되므로, 빌트인 모듈의
 * 친숙한 이름("text-adventure" 등)이나 다른 사용자 카드와 충돌하지 않게 한다.
 * (Chat.moduleId가 이 UUID를 가리키게 되어 세션↔모듈 해석이 안정적이다.)
 */
export function createGameCard(
  init: Partial<GameCard> & { definition: GameDefinition },
): GameCard {
  const id = newGameCardId();
  const definition = { ...init.definition, id };
  return {
    ...init,
    id,
    sync: newSyncMeta(),
    definition,
    document: init.document ?? gameDefinitionToRoqeetJson(definition),
    enabled: init.enabled ?? true,
  };
}
